import { useEffect, useState } from 'react'
import { ArchetypeEquipmentType, InstanceEquipmentType } from '../types/equipment-manager/types'
import { fetchArchetypes, fetchEquipments } from '../services/api'
import { SelectOptions } from '../types'

export const RIG_FLOOR_EVENT_NAME = 'Rig Floor Output Event Type'

export const useEquipments = ({
    rigId,
    eventType,
    setAdditionalOutputEventName,
    setAdditionalOutputEventValue
}: {
    rigId: string
    eventType: string | null
    setAdditionalOutputEventName: React.Dispatch<React.SetStateAction<string>>
    setAdditionalOutputEventValue: React.Dispatch<React.SetStateAction<string | null>>
}): {
    hydratongEquipments: SelectOptions[]
    hydrarackerEquipments: SelectOptions[]
    topDriveEquipments: SelectOptions[]
    rrtEquipments: SelectOptions[]
    catwalkEquipments: SelectOptions[]
    setHydratongEquipments: React.Dispatch<React.SetStateAction<SelectOptions[]>>
    setHydrarackerEquipments: React.Dispatch<React.SetStateAction<SelectOptions[]>>
    setTopDriveEquipments: React.Dispatch<React.SetStateAction<SelectOptions[]>>
    setRrtEquipments: React.Dispatch<React.SetStateAction<SelectOptions[]>>
    setCatwalkEquipments: React.Dispatch<React.SetStateAction<SelectOptions[]>>
} => {
    const [hydratongEquipments, setHydratongEquipments] = useState<SelectOptions[]>([])
    const [hydrarackerEquipments, setHydrarackerEquipments] = useState<SelectOptions[]>([])
    const [topDriveEquipments, setTopDriveEquipments] = useState<SelectOptions[]>([])
    const [rrtEquipments, setRrtEquipments] = useState<SelectOptions[]>([])
    const [catwalkEquipments, setCatwalkEquipments] = useState<SelectOptions[]>([])

    useEffect(() => {
        const loadEquipmentData = async (): Promise<void> => {
            try {
                const archetypesRes = await fetchArchetypes()
                if (!archetypesRes.ok) return

                const rawArchetypes: ArchetypeEquipmentType[] = await archetypesRes.json()
                const archetypes = buildArchetypesInfo(rawArchetypes)

                const equipmentsRes = await fetchEquipments()
                if (!equipmentsRes.ok) return

                const rawEquipments: InstanceEquipmentType[] = await equipmentsRes.json()
                const equipments = buildEquipmentsInfo(rawEquipments, archetypes)

                setHydratongEquipments(buildEquipmentsForSelect(equipments.hydratongEquipments))
                setHydrarackerEquipments(buildEquipmentsForSelect(equipments.hydrarackerEquipments))
                setTopDriveEquipments(buildEquipmentsForSelect(equipments.topDriveEquipments))
                setRrtEquipments(buildEquipmentsForSelect(equipments.rrtEquipments))
                setCatwalkEquipments(buildEquipmentsForSelect(equipments.catwalkEquipments))
            } catch (err) {
                console.error('Failed to load equipment data:', err)
            }
        }

        loadEquipmentData()

        setAdditionalOutputEventName(RIG_FLOOR_EVENT_NAME)

        if (eventType) {
            setAdditionalOutputEventValue(`${eventType}_rigfloor, ${eventType}_kpi_rigfloor`)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [eventType])

    const isHydratong = (name: string): boolean => {
        return name === 'Hydratong_KPI_Main' || name === 'Hydratong_KPI_Aux'
    }

    const isHydraracker = (name: string): boolean => {
        return name === 'Hydraracker_KPI_Main' || name === 'Hydraracker_KPI_Aux'
    }

    const isTopDrive = (name: string): boolean => {
        return name === 'Top_Drive_KPI_Main' || name === 'Top_Drive_KPI_Aux'
    }

    const isRrt = (name: string): boolean => {
        return name === 'RRT_KPI_Main' || name === 'RRT_KPI_Aux'
    }

    const isCatwalk = (name: string): boolean => {
        return name === 'Catwalk_KPI_Main' || name === 'Catwalk_KPI_Aux'
    }

    const isValidArchetype = (name: string): boolean => {
        return isHydratong(name) || isHydraracker(name) || isTopDrive(name) || isRrt(name) || isCatwalk(name)
    }

    const buildArchetypesInfo = (rawData: ArchetypeEquipmentType[]): { [key: string]: string } => {
        const ret: { [key: string]: string } = {}

        rawData.forEach(it => {
            const uuid = it?.uuid
            const name = it?.description?.identifier?.name

            if (uuid && name && isValidArchetype(name)) {
                ret[uuid] = name
            }
        })

        return ret
    }

    const buildEquipmentsInfo = (
        rawData: InstanceEquipmentType[],
        archetypes: { [key: string]: string }
    ): {
        hydratongEquipments: InstanceEquipmentType[]
        hydrarackerEquipments: InstanceEquipmentType[]
        topDriveEquipments: InstanceEquipmentType[]
        rrtEquipments: InstanceEquipmentType[]
        catwalkEquipments: InstanceEquipmentType[]
    } => {
        const hydratongEquipments: InstanceEquipmentType[] = []
        const hydrarackerEquipments: InstanceEquipmentType[] = []
        const topDriveEquipments: InstanceEquipmentType[] = []
        const rrtEquipments: InstanceEquipmentType[] = []
        const catwalkEquipments: InstanceEquipmentType[] = []

        rawData.forEach(it => {
            const assetInfo = getEquipmentAssetInfo(it)
            if (assetInfo !== null) {
                const isLinked = assetInfo.assetId === rigId
                const isRig = assetInfo.assetType.toUpperCase() === 'RIG'
                const associatedArchetype = archetypes[it?.archetypeUUID] ?? null

                if (isLinked && isRig && associatedArchetype !== null) {
                    if (isHydratong(associatedArchetype)) {
                        hydratongEquipments.push(it)
                    }

                    if (isHydraracker(associatedArchetype)) {
                        hydrarackerEquipments.push(it)
                    }

                    if (isTopDrive(associatedArchetype)) {
                        topDriveEquipments.push(it)
                    }

                    if (isRrt(associatedArchetype)) {
                        rrtEquipments.push(it)
                    }

                    if (isCatwalk(associatedArchetype)) {
                        catwalkEquipments.push(it)
                    }
                }
            }
        })

        return { hydratongEquipments, hydrarackerEquipments, topDriveEquipments, rrtEquipments, catwalkEquipments }
    }

    const buildEquipmentsForSelect = (equipments: InstanceEquipmentType[]): { value: string; label: string }[] => {
        const ret: { value: string; label: string }[] = []

        equipments.forEach(equipment => {
            const uuid = equipment?.uuid
            const name = equipment?.description?.identifier?.alias

            if (uuid && name) {
                ret.push({ value: uuid, label: name })
            }
        })

        return ret
    }

    const getEquipmentAssetInfo = (equipment: InstanceEquipmentType): { assetId: string; assetType: string } | null => {
        const components = equipment?.components

        if (!components || Object.keys(components).length === 0) {
            return null
        }

        const firstComponent = Object.values(components)[0]
        const assetId = firstComponent?.assetId
        const assetType = firstComponent?.assetType

        if (!assetId || !assetType) {
            return null
        }

        return { assetId, assetType }
    }

    return {
        hydratongEquipments,
        hydrarackerEquipments,
        topDriveEquipments,
        rrtEquipments,
        catwalkEquipments,
        setHydratongEquipments,
        setHydrarackerEquipments,
        setTopDriveEquipments,
        setRrtEquipments,
        setCatwalkEquipments
    }
}
